import {
  Badge,
  Button,
  Code,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { ApiError } from "../../api/errors";
import {
  getListStaffAiTasksQueryKey,
  useListStaffAiTasks,
  useSetStaffAiTaskModel,
} from "../../api/generated/endpoints/staff/staff";
import type { AiTaskModelView, AiTaskView, PriceLine } from "../../api/generated/model";
import { ValidationDetails } from "../../api/generated/zod";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { useIdempotencyKey } from "../../platform/idempotency";
import { toUserMessage } from "../../platform/messages";
import { formatDateTime } from "../../ui/format";

const REASON = "Say why the model changes.";
/** What billing's units read per: "16 credits an output"; an unknown unit reads as itself. */
const PER: Readonly<Record<string, string>> = {
  image: "an image",
  video: "a video",
  output: "an output",
  second: "a second",
  character: "a character",
  token: "a token",
  request: "a request",
  render: "a render",
};

/** A decimal string that is nothing: "0", "0.0", "0.000". */
const ZERO = /^0+(\.0+)?$/;

/**
 * A row's price in words (G9 section 8): "free, 30 an hour", "16 credits an output", "8 credits
 * per 450 characters", "per token: 0.003637 written, 0.000727 read". The credits are billing's
 * decimal strings as they come, never float arithmetic.
 */
export function priceText(price: PriceLine): string {
  const cap = price.free_hourly_cap ?? null;
  if (ZERO.test(price.credits_per_unit)) return cap === null ? "free" : `free, ${cap} an hour`;
  const input = price.input ?? null;
  if (input !== null) {
    return `per ${price.unit}: ${price.credits_per_unit} written, ${input.credits_per_unit} read`;
  }
  if (price.block_size !== undefined && price.block_size !== null && price.block_size !== "1") {
    return `${price.credits_per_unit} credits per ${price.block_size} ${price.unit}s`;
  }
  return `${price.credits_per_unit} credits ${PER[price.unit] ?? `per ${price.unit}`}`;
}

/** The field a refusal names: the reason, or the picker for anything else (G9 section 8). */
function fieldOf(error: unknown): "reason" | "model" | null {
  if (!(error instanceof ApiError) || error.code !== "VALIDATION_FAILED") return null;
  const details = ValidationDetails.safeParse(error.details);
  const loc = details.success ? (details.data.errors[0]?.loc ?? []) : [];
  return loc.includes("reason") ? "reason" : "model";
}

/**
 * Staff's "AI tasks" (G9 section 8, issue 433): every step that runs a model the user does not
 * pick, with live's model and the one it runs on now, and the picker that switches it for every
 * job started after, with the reason the audit log keeps.
 */
export function StaffAiTasksScreen() {
  const tasks = useListStaffAiTasks();
  const items = tasks.data?.items ?? [];
  return (
    <Stack gap="sm">
      <Text size="sm" c="var(--cai-color-text-muted)">
        A switch applies to every job started after it; running jobs keep their model.
      </Text>
      {tasks.isPending ? <Loader size="sm" aria-label="Loading the AI tasks" /> : null}
      {tasks.isError ? <ErrorAlert error={tasks.error} /> : null}
      {items.length === 0 ? null : (
        <Table.ScrollContainer minWidth={760}>
          <Table verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Task</Table.Th>
                <Table.Th>Live's model</Table.Th>
                <Table.Th>Now</Table.Th>
                <Table.Th>Switch to</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((task) => (
                <TaskRow key={task.id} task={task} />
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}

const nameOf = (task: AiTaskView, model: string) =>
  task.models.find((each) => each.model === model)?.name ?? model;

function TaskRow({ task }: { task: AiTaskView }) {
  const { override } = task;
  return (
    <Table.Tr aria-label={task.name}>
      <Table.Td>
        <Stack gap={4}>
          <Text fw={500}>{task.name}</Text>
          <Text size="sm">{task.description}</Text>
          <Text size="xs" c="var(--cai-color-text-muted)">
            {task.job_type ?? `Run by the ${task.caller ?? "?"}`}
          </Text>
          <Group gap={4}>
            {task.prompts.map((prompt) => (
              <Code key={prompt}>{prompt}</Code>
            ))}
          </Group>
        </Stack>
      </Table.Td>
      <Table.Td>{nameOf(task, task.live_model)}</Table.Td>
      <Table.Td>
        <Stack gap={4}>
          <Text>{nameOf(task, task.current_model)}</Text>
          {override === null ? (
            <Badge variant="light" color="gray">
              live's default
            </Badge>
          ) : (
            <>
              <Group gap={4}>
                <Badge variant="light">switched</Badge>
                {task.stale ? (
                  <Badge variant="light" color="orange">
                    stale
                  </Badge>
                ) : null}
              </Group>
              <Text size="xs">
                {override.set_by.email ?? override.set_by.user_id}, {formatDateTime(override.set_at)}
              </Text>
              <Text size="xs" c="var(--cai-color-text-muted)">
                {task.stale
                  ? `${nameOf(task, override.model)} left this task's list, so live's default runs.`
                  : override.reason}
              </Text>
            </>
          )}
        </Stack>
      </Table.Td>
      <Table.Td>
        {task.picks === "catalog" ? (
          <Text size="sm" c="var(--cai-color-text-muted)">
            Switched by a catalog PR.
          </Text>
        ) : (
          // A new model read from the list starts the picker over at it.
          <TaskSwitch key={task.current_model} task={task} />
        )}
      </Table.Td>
    </Table.Tr>
  );
}

/**
 * A row's choice in the picker: its name, vendor and price ("no price" when billing has none),
 * then its reason: why it cannot be chosen here, or a hint about a row that can (w5's PR 2).
 */
function optionOf(model: AiTaskModelView) {
  const price = model.price === null ? "no price" : priceText(model.price);
  const label = `${model.name} · ${model.vendor} · ${price}`;
  const reason = model.reason ?? (model.selectable ? null : "Not offered here.");
  return {
    value: model.model,
    label: reason === null ? label : `${label} (${reason})`,
    disabled: !model.selectable,
  };
}

/**
 * The picker, the reason and "Switch", and "Use live's default" (G9 section 8): each sends the
 * row and the reason with a key of its own; the list is read again after. A refusal shows on the
 * field it names.
 */
function TaskSwitch({ task }: { task: AiTaskView }) {
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [model, setModel] = useState(task.current_model);
  const [reason, setReason] = useState("");
  const [missing, setMissing] = useState(false);
  const set = useSetStaffAiTaskModel({
    mutation: {
      onSuccess: () => {
        setReason("");
        return queryClient.invalidateQueries({ queryKey: getListStaffAiTasksQueryKey() });
      },
    },
  });
  const send = (chosen: string) => {
    if (reason.trim() === "") {
      setMissing(true);
      return;
    }
    setMissing(false);
    const data = { model: chosen, reason: reason.trim() };
    const key = idempotency.keyFor(data);
    set.mutate(
      { taskId: task.id, data, headers: { "Idempotency-Key": key } },
      { onError: (error) => idempotency.failed(key, data, error) },
    );
  };
  const field = set.isError ? fieldOf(set.error) : null;
  const refusal = set.isError ? toUserMessage(set.error) : undefined;
  return (
    <form
      noValidate
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        send(model);
      }}
    >
      <Stack gap="xs" maw={420}>
        <Select
          label={`Model for ${task.name}`}
          data={task.models.map(optionOf)}
          // A row that can be chosen may still say something, such as who runs it.
          description={task.models.find((each) => each.model === model && each.selectable)?.reason}
          value={model}
          allowDeselect={false}
          onChange={(next) => {
            if (next !== null) setModel(next);
          }}
          error={field === "model" ? refusal : undefined}
        />
        <TextInput
          label="Reason"
          value={reason}
          maxLength={500}
          onChange={(event) => setReason(event.currentTarget.value)}
          error={missing ? REASON : field === "reason" ? refusal : undefined}
        />
        <Group gap="xs">
          <Button type="submit" loading={set.isPending} disabled={model === task.current_model}>
            Switch
          </Button>
          <Button
            variant="light"
            // A stale switch runs live's model already, but its row stays until cleared.
            disabled={set.isPending || (task.current_model === task.live_model && !task.stale)}
            onClick={() => send(task.live_model)}
          >
            Use live's default
          </Button>
        </Group>
        {set.isError && field === null ? <ErrorAlert error={set.error} /> : null}
      </Stack>
    </form>
  );
}
